import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Plus,
  Minus,
  Clock,
  DollarSign,
  Globe2,
  ShieldCheck,
  Scaling,
  CalendarCheck,
  Fan,
  Wrench,
  Zap,
  Home,
  HardHat,
} from "lucide-react";
import Navbar from "../../components/site/Navbar";
import Footer from "../../components/site/Footer";
import useReveal from "../../lib/useReveal";

const CALENDLY = "https://cal.com/rggsales/30min";

/* --------------------------------- Pieces --------------------------------- */

const Eyebrow = ({ children, tone = "green" }) =>
  tone === "green" ? (
    <span className="inline-flex items-center rounded-full bg-[#E7F6EE] border border-[#16A06A]/20 px-3.5 py-1.5">
      <span className="eyebrow text-[#16A06A]">{children}</span>
    </span>
  ) : (
    <span className="eyebrow text-slate-400">{children}</span>
  );

const PrimaryCTA = ({ children = "Book a Call", testid, fullWidth = false }) => (
  <a
    href={CALENDLY}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={testid}
    className={`group inline-flex items-center justify-center gap-2 rounded-lg bg-[#16A06A] hover:bg-[#0E7C53] text-white px-6 py-3.5 text-[15px] font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] hover:shadow-[0_8px_24px_rgba(22,160,106,0.35)] transition-all ${
      fullWidth ? "w-full" : ""
    }`}
  >
    {children}
    <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
  </a>
);

/* ---------------------------------- Hero ---------------------------------- */

const Hero = () => (
  <section data-testid="ea-hero" className="relative pt-32 lg:pt-40 pb-20 lg:pb-24 overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
      <div className="absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-[rgba(22,160,106,0.08)] blur-3xl" />
      <div className="absolute top-32 -left-40 h-[420px] w-[420px] rounded-full bg-[rgba(10,22,40,0.05)] blur-3xl" />
    </div>

    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <Link
        to="/services"
        data-testid="ea-hero-back"
        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#16A06A] hover:text-[#0E7C53] reveal"
      >
        <ArrowLeft size={14} />
        All services
      </Link>

      <div className="mt-5 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-7 reveal">
          <Eyebrow>Executive Assistants</Eyebrow>
          <h1 className="h1-hero mt-5 text-[#0A1628]">
            Hire an Executive Assistant and{" "}
            <span className="text-[#16A06A]">Reclaim Your Time.</span>
          </h1>
          <p className="mt-6 text-[18px] leading-[1.65] text-slate-500 max-w-[620px]">
            We match you with a full-time executive assistant in
            about three weeks — so you can focus on growth instead of admin.
          </p>

          <div className="mt-8">
            <PrimaryCTA testid="ea-hero-cta" />
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
            {[
              "3-week match",
              "Month-to-month",
            ].map((c) => (
              <div key={c} className="inline-flex items-center gap-2 text-[14px] text-slate-600">
                <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#E7F6EE]">
                  <Check size={12} className="text-[#16A06A]" strokeWidth={3} />
                </span>
                {c}
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 reveal">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-2xl overflow-hidden border border-slate-200 card-soft bg-white aspect-[3/4]">
              <img
                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=900&q=80"
                alt="Executive assistant on a video call"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
            <div className="flex flex-col gap-3">
              <div className="rounded-2xl overflow-hidden border border-slate-200 card-soft bg-white aspect-square">
                <img
                  src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=900&q=80"
                  alt="Assistant managing calendar"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="rounded-xl bg-white border border-slate-200 card-soft p-4">
                <div className="text-[22px] font-bold text-[#0A1628] leading-none">
                  16 <span className="text-[13px] font-semibold text-slate-500">hrs/wk</span>
                </div>
                <p className="mt-1.5 text-[11px] leading-snug text-slate-500">
                  lost to admin by small business owners
                </p>
                <p className="mt-1 text-[9px] uppercase tracking-wider text-slate-400 font-semibold">
                  Source: Time Etc
                </p>
              </div>
              <div className="rounded-xl bg-[#0A1628] text-white p-4">
                <div className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">
                  Match Time
                </div>
                <div className="mt-1 text-[28px] font-bold leading-none">3 weeks</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* --------------------------------- Benefits -------------------------------- */

const BENEFITS = [
  {
    icon: Clock,
    title: "Reclaim Time Lost to Admin",
    body:
      "Focus on strategy and growth while your assistant manages email, calendars, travel, and follow-ups.",
  },
  {
    icon: DollarSign,
    title: "Cut Admin Costs Significantly",
    body:
      "A flat monthly engagement replaces U.S. salary, payroll taxes, and overhead — no hidden hours or surcharges.",
  },
  {
    icon: Globe2,
    title: "Work in Your Time Zone",
    body:
      "Assistants based in Latin America overlap U.S. business hours for real-time collaboration and faster turnarounds.",
  },
  {
    icon: ShieldCheck,
    title: "Security That Fits Your Tools",
    body:
      "Multi-factor authentication, role-based access to your systems, VPN where you require it, and a signed NDA before anyone touches an account.",
  },
  {
    icon: Scaling,
    title: "Scale on Demand",
    body:
      "Add or reduce support month-to-month; your dedicated account manager handles changes without extra recruiting effort.",
  },
  {
    icon: CalendarCheck,
    title: "3-week match",
    body:
      "Interview a shortlist of vetted candidates and start working in week three.",
  },
];

const Benefits = () => (
  <section data-testid="ea-benefits" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Benefits</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Why Leaders <span className="text-[#16A06A]">Choose RevGen</span>{" "}
          Executive Assistants.
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {BENEFITS.map((b, i) => {
          const Icon = b.icon;
          return (
            <article
              key={b.title}
              data-testid={`ea-benefit-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[18px] font-semibold text-[#0A1628]">{b.title}</h3>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{b.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* ------------------------------ How it works ------------------------------ */

const STEPS = [
  {
    n: "01",
    title: "Discover Your Needs",
    body:
      "Book a 30-minute strategy call where we map tasks, tools, and ideal working hours.",
  },
  {
    n: "02",
    title: "Meet Vetted Talent",
    body:
      "After two weeks of recruiting and screening against your brief, you receive a shortlist of 3–5 Tier A executive assistants to interview.",
  },
  {
    n: "03",
    title: "Launch & Integrate",
    body:
      "Your chosen assistant gains secure access to email, calendar, and project tools, and follows a 90-day success roadmap.",
  },
];

const AFTER_HIRE = [
  {
    title: "Sign & Start",
    body:
      "Approve a simple month-to-month agreement and your assistant begins immediately.",
  },
  {
    title: "Optimize & Scale",
    body:
      "Weekly check-ins and quarterly reviews refine workflows and let you adjust support as you grow.",
  },
];

const HowItWorks = () => (
  <section data-testid="ea-how" className="py-24 lg:py-28 bg-[#F8FAFC]">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Process</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          How to Hire Your Executive Assistant in 3 Simple Steps.
        </h2>
      </div>

      <div className="mt-14 grid md:grid-cols-3 gap-5">
        {STEPS.map((s, i) => (
          <article
            key={s.n}
            data-testid={`ea-step-${s.n}`}
            className="reveal bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <span className="text-[42px] font-bold leading-none text-[#16A06A]/20">
              {s.n}
            </span>
            <h3 className="mt-4 text-[19px] font-semibold text-[#0A1628]">{s.title}</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-slate-500">{s.body}</p>
          </article>
        ))}
      </div>

      {/* After You Hire sub-band */}
      <div className="reveal mt-14 rounded-2xl bg-white border border-slate-200 card-soft overflow-hidden">
        <div className="p-7 lg:p-10">
          <Eyebrow tone="muted">After You Hire</Eyebrow>
          <div className="mt-5 grid sm:grid-cols-2 gap-6">
            {AFTER_HIRE.map((a, i) => (
              <div key={a.title} data-testid={`ea-after-${i + 1}`}>
                <h4 className="text-[16px] font-semibold text-[#0A1628]">{a.title}</h4>
                <p className="mt-2 text-[14px] leading-relaxed text-slate-500">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------------------- Industries ------------------------------- */

const INDUSTRIES = [
  {
    icon: Fan,
    title: "HVAC",
    body:
      "Keep the dispatch board honest: schedule confirmations, maintenance-agreement renewals, and the follow-ups nobody gets to after a hot week.",
  },
  {
    icon: Wrench,
    title: "Plumbing",
    body:
      "Intake, quote follow-up, and permit paperwork handled, so the callback doesn't sit in a voicemail box until Thursday.",
  },
  {
    icon: Zap,
    title: "Electrical",
    body:
      "Job packets assembled, inspections booked, and change orders documented before they turn into an argument about scope.",
  },
  {
    icon: Home,
    title: "Roofing",
    body:
      "Insurance paperwork, supplement follow-up, and homeowner updates kept moving through a job that touches five parties.",
  },
  {
    icon: HardHat,
    title: "General Contracting",
    body:
      "Subcontractor scheduling, lien waivers, and job-cost tracking kept current across every open project.",
  },
];

const Industries = () => (
  <section data-testid="ea-industries" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Industries</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Designed for Every Type of Leader.
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Executive assistant support tailored to your industry.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {INDUSTRIES.map((s, i) => {
          const Icon = s.icon;
          return (
            <article
              key={s.title}
              data-testid={`ea-industry-${i + 1}`}
              className="reveal group bg-white rounded-2xl border border-slate-200 p-7 card-soft hover:-translate-y-1 hover:border-[#16A06A]/40 transition-all"
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#E7F6EE] text-[#16A06A] group-hover:bg-[#16A06A] group-hover:text-white transition-colors">
                <Icon size={18} />
              </div>
              <h3 className="mt-5 text-[18px] font-semibold text-[#0A1628]">{s.title}</h3>
              <p className="mt-3 text-[14px] leading-relaxed text-slate-500">{s.body}</p>
            </article>
          );
        })}
      </div>
    </div>
  </section>
);

/* --------------------------------- Pricing -------------------------------- */

const PLAN_INCLUDES = [
  "3-week match",
  "Month-to-month engagement",
  "MFA and role-based access",
  "Weekly academy sessions",
  "Weekly check-ins + dedicated manager",
];

const Pricing = () => (
  <section data-testid="ea-pricing" className="py-24 lg:py-28">
    <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
      <div className="max-w-3xl reveal">
        <Eyebrow tone="muted">Engagements</Eyebrow>
        <h2 className="h2-section mt-3 text-[#0A1628]">
          Dedicated Support, <span className="text-[#16A06A]">Without the Overhead.</span>
        </h2>
        <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
          Flexible part-time and full-time engagements that grow with your
          workload — every option backed by MFA and role-based access,
          hands-on support, and continuous upskilling. Pricing is tailored to
          your scope.
        </p>
      </div>

      <div className="mt-14 grid md:grid-cols-2 gap-6">
        {[
          {
            tag: "Part-Time",
            hours: "~20 hrs / week dedicated",
            testid: "ea-plan-part-time",
            highlighted: false,
          },
          {
            tag: "Full-Time",
            hours: "~40 hrs / week dedicated",
            testid: "ea-plan-full-time",
            highlighted: true,
          },
        ].map((p, i) => (
          <article
            key={p.tag}
            data-testid={p.testid}
            className={`reveal relative flex flex-col rounded-2xl p-8 lg:p-10 border card-soft transition-all hover:-translate-y-1 ${
              p.highlighted
                ? "bg-[#0A1628] text-white border-[#0A1628]"
                : "bg-white border-slate-200"
            }`}
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            <span
              className={`eyebrow ${
                p.highlighted ? "text-[#34D399]" : "text-[#16A06A]"
              }`}
            >
              {p.tag}
            </span>
            <h3
              className={`mt-3 text-[26px] lg:text-[30px] font-bold tracking-tight ${
                p.highlighted ? "text-white" : "text-[#0A1628]"
              }`}
            >
              {p.hours}
            </h3>
            <p
              className={`mt-2 text-[14px] ${
                p.highlighted ? "text-white/70" : "text-slate-500"
              }`}
            >
              Custom-scoped to your workload and tools.
            </p>

            <ul className="mt-7 space-y-3 flex-1">
              {PLAN_INCLUDES.map((inc) => (
                <li
                  key={inc}
                  className={`flex items-start gap-3 text-[14px] ${
                    p.highlighted ? "text-white/85" : "text-slate-600"
                  }`}
                >
                  <span
                    className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                      p.highlighted
                        ? "bg-[#16A06A] text-white"
                        : "bg-[#E7F6EE] text-[#16A06A]"
                    }`}
                  >
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {inc}
                </li>
              ))}
            </ul>

            <div className="mt-8">
              <PrimaryCTA testid={`${p.testid}-cta`} fullWidth>
                Book a Call
              </PrimaryCTA>
            </div>
          </article>
        ))}
      </div>

      <p className="reveal mt-6 text-[13px] text-slate-400 text-center">
        Custom pricing based on scope and hours — book a call for a quote.
      </p>
    </div>
  </section>
);

/* ---------------------------------- Tools --------------------------------- */

const TOOLS = ["Slack", "HubSpot", "Trello", "Gmail", "Google Calendar", "Outlook", "Notion", "Asana"];

const Tools = () => {
  const repeated = Array.from({ length: 4 }, () => TOOLS).flat();
  return (
    <section data-testid="ea-tools" className="py-24 lg:py-28 bg-[#F8FAFC]">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="max-w-3xl reveal">
          <Eyebrow tone="muted">Works How You Work</Eyebrow>
          <h2 className="h2-section mt-3 text-[#0A1628]">Plug In. Power Up.</h2>
          <p className="mt-5 text-[17px] leading-relaxed text-slate-500">
            We adapt to how you already operate — your assistant uses what you
            use. We don't force tools; we adapt to yours.
          </p>
        </div>

        <div className="mt-12 overflow-hidden marquee-mask">
          <div className="marquee-track flex items-center gap-4">
            {repeated.map((name, i) => (
              <span
                key={`${name}-${i}`}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-white border border-slate-200 px-5 py-3 text-[15px] font-semibold text-slate-500 hover:text-[#0A1628] hover:border-[#16A06A]/40 transition-colors card-soft"
              >
                <span className="h-2 w-2 rounded-full bg-[#16A06A]" />
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ----------------------------------- FAQ ---------------------------------- */

const FAQS = [
  {
    q: "How quickly can I hire an executive assistant?",
    a: "Most clients start in about three weeks. We recruit and screen against your brief across weeks one and two, send you a shortlist of 3–5 Tier A candidates with their resumes, schedule interviews at your convenience, and begin onboarding once you sign the month-to-month agreement.",
  },
  {
    q: "What does onboarding involve?",
    a: "Your assistant completes a secure setup checklist (VPN, MFA, role-based tool access) then follows a 90-day success roadmap covering task priorities, communication rhythms, and KPIs.",
  },
  {
    q: "Which tasks can a remote executive assistant handle?",
    a: "Email triage, calendar control, travel bookings, project coordination, CRM updates, invoice follow-up, light research, and customer or vendor communications.",
  },
  {
    q: "Which industries do you cover?",
    a: "HVAC, plumbing, electrical, roofing, landscaping, remodelling and general contracting — matched to someone who already knows the tools your trade runs on.",
  },
  {
    q: "How much does it cost?",
    a: "Pricing is tailored to your scope and hours. Book a quick call and we'll give you a clear quote.",
  },
  {
    q: "How do you compare to hiring in-house?",
    a: "One flat monthly fee replaces the overhead of hiring, employing and covering someone — recruiting, payroll, benefits and cover sit with us. Your assistant typically starts in about three weeks rather than the months an in-house search takes. If the fit is wrong or they move on, we replace them within two weeks.",
  },
  {
    q: "Are there long-term contracts or setup fees?",
    a: "No. All plans are month-to-month with zero setup charges, and you can scale hours up or down with 30-day notice.",
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="ea-faq" className="py-24 lg:py-28">
      <div className="mx-auto max-w-[1200px] px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 reveal">
            <Eyebrow tone="muted">FAQ</Eyebrow>
            <h2 className="h2-section mt-3 text-[#0A1628]">
              Executive Assistant Services — Frequently Asked Questions.
            </h2>
            <p className="mt-5 text-[15px] leading-relaxed text-slate-500">
              Still unsure?{" "}
              <a
                href={CALENDLY}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#16A06A] font-semibold hover:text-[#0E7C53]"
              >
                Book a 30-min call →
              </a>
            </p>
          </div>

          <div className="lg:col-span-8 reveal">
            <div className="rounded-2xl border border-slate-200 bg-white card-soft divide-y divide-slate-200 overflow-hidden">
              {FAQS.map((f, i) => {
                const isOpen = open === i;
                return (
                  <div key={f.q} data-testid={`ea-faq-item-${i + 1}`}>
                    <button
                      type="button"
                      onClick={() => setOpen(isOpen ? -1 : i)}
                      aria-expanded={isOpen}
                      data-testid={`ea-faq-toggle-${i + 1}`}
                      className="w-full flex items-center justify-between gap-6 px-6 md:px-8 py-6 text-left hover:bg-[#FAFAFA] transition-colors"
                    >
                      <span className="text-[16px] md:text-[17px] font-semibold text-[#0A1628]">
                        {f.q}
                      </span>
                      <span
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
                          isOpen ? "bg-[#16A06A] text-white" : "bg-[#E7F6EE] text-[#16A06A]"
                        }`}
                      >
                        {isOpen ? <Minus size={16} strokeWidth={3} /> : <Plus size={16} strokeWidth={3} />}
                      </span>
                    </button>
                    <div
                      className={`grid transition-all duration-300 ease-out ${
                        isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                      }`}
                    >
                      <div className="overflow-hidden">
                        <p className="px-6 md:px-8 pb-6 text-[15px] md:text-[16px] leading-relaxed text-slate-500">
                          {f.a}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* -------------------------------- Final CTA -------------------------------- */

const FinalCTA = () => (
  <section data-testid="ea-final-cta" className="relative bg-[#0A1628] overflow-hidden">
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <div className="absolute -top-24 left-1/4 h-[360px] w-[360px] rounded-full bg-[#16A06A]/15 blur-3xl" />
      <div className="absolute -bottom-32 right-10 h-[420px] w-[420px] rounded-full bg-[#16A06A]/10 blur-3xl" />
    </div>
    <div className="relative mx-auto max-w-[1200px] px-6 lg:px-8 py-24 lg:py-28 text-center">
      <div className="reveal">
        <span className="eyebrow text-[#16A06A]">Get started</span>
        <h2 className="h2-section mt-3 text-white max-w-3xl mx-auto">
          Ready to <span className="text-[#34D399]">Reclaim Your Calendar?</span>
        </h2>
        <p className="mt-5 text-[17px] lg:text-[18px] leading-relaxed text-slate-300 max-w-2xl mx-auto">
          Book a quick consult, choose your assistant from the shortlist, and
          start handing off admin in week three.
        </p>
        <div className="mt-9 flex justify-center">
          <PrimaryCTA testid="ea-final-cta-btn" />
        </div>
      </div>
    </div>
  </section>
);

/* ---------------------------------- Page ---------------------------------- */

export default function ExecutiveAssistants() {
  useReveal();

  useEffect(() => {
    document.title = "Executive Assistants | RevGen";
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        "content",
        "A trained executive assistant working inside your business, typically starting in about three weeks. We recruit, employ and pay them — you direct the work. Book a free assessment."
      );
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div data-testid="executive-assistants-page" className="bg-white text-[#0A1628]">
      <Navbar />
      <main>
        <Hero />
        <Benefits />
        <HowItWorks />
        <Industries />
        <Pricing />
        <Tools />
        <FAQ />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}

